// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { z } from 'zod';

export type LocalStorageValue<Schema extends z.ZodTypeAny> = z.infer<Schema>;

/**
 * Dispatches a storage event with the provided key and new value.
 *
 * @param key - The key to dispatch in the storage event.
 * @param newValue - The new value to dispatch in the storage event.
 */
function dispatchStorageEvent(key: string, newValue: string | null) {
  const event = new StorageEvent('storage', { key, newValue });

  window.dispatchEvent(event);
}

/**
 * Retrieves a value from local storage and parses it using the provided schema.
 * If the value is not present in local storage or cannot be parsed, this
 * function returns null.
 *
 * @param key - The key to retrieve from local storage.
 * @param schema - The schema to use to parse the value.
 */
export function getLocalStorageValue<Schema extends z.ZodTypeAny>(
  key: string,
  schema: Schema
): LocalStorageValue<Schema> | null {
  const value = window.localStorage.getItem(key);

  if (value === null) {
    return null;
  }

  try {
    // Parse the value using the provided schema. Zod schemas used in this way
    // can't infer the type of the parsed value, so this gets flagged by ESLint
    // even though it's safe.
    //
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return schema.parse(JSON.parse(value));

    // This error is intentionally discarded. JSON.parse can throw an error if
    // the string provided doesn't contain valid JSON. The schema parser can
    // also throw an error if the schema fails to parse. In both cases, this is
    // simply considered an invalid value.
  } catch (_) {
    return null;
  }
}

/**
 * Sets a value in local storage after stringifying it and dispatches a storage
 * event.
 *
 * @param key - The key to set in local storage.
 * @param value - The value to set in local storage.
 */
export function setLocalStorageValue(key: string, value: unknown) {
  const serializedValue = JSON.stringify(value);

  window.localStorage.setItem(key, serializedValue);

  dispatchStorageEvent(key, serializedValue);
}

/**
 * Removes a value from local storage and dispatches a storage event.
 *
 * @param key - The key to remove from local storage.
 */
export function removeLocalStorageValue(key: string) {
  window.localStorage.removeItem(key);

  dispatchStorageEvent(key, null);
}
